import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getContractor, createContractorDocument } from "../services/contractorsService";
import { validateDocumentFile, getDocumentAcceptValue, getDocumentHelpText } from "../utils/documentValidation";
import styles from "./ContractorDocumentCreate.module.css";

const ContractorDocumentCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    document: null,
  });
  const [formErrors, setFormErrors] = useState({});

  // Load contractor data
  useEffect(() => {
    loadContractorData();
  }, [id]);

  const loadContractorData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getContractor(id);
      if (response.success) {
        setContractor(response.data);
      } else {
        throw new Error("Failed to load contractor");
      }
    } catch (error) {
      console.error("Error loading contractor data:", error);
      setError("Error loading contractor data");
      toast.error("Error loading contractor data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFormData(prev => ({
        ...prev,
        document: null
      }));
      return;
    }

    // Validate file
    const validationResult = validateDocumentFile(file);
    if (!validationResult.isValid) {
      toast.error(validationResult.error);
      e.target.value = "";
      return;
    }

    setFormData(prev => ({
      ...prev,
      document: file
    }));

    // Clear error when user selects a file
    if (formErrors.document) {
      setFormErrors(prev => ({
        ...prev,
        document: ""
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Document is required
    if (!formData.document) {
      errors.document = "Document is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("contractor_id", contractor.id);
      submitData.append("name", formData.name);
      submitData.append("document", formData.document);

      const response = await createContractorDocument(id, submitData);
      
      if (response.success) {
        toast.success("Document added successfully!");
        navigate(`/contractors/${id}`);
      } else {
        throw new Error(response.message || "Failed to add document");
      }
    } catch (error) {
      console.error("Error creating document:", error);
      toast.error(error.message || "Error creating document");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/contractors/${id}`);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={handleCancel} className={styles.btnSecondary}>
            Back to Contractor
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.breadcrumbs}>
        <span>Contractors</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span>{contractor?.name}</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span>Add Document</span>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Add document to contractor</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <fieldset className={styles.fieldset}>
                  <input 
                    type="hidden" 
                    name="contractor_id" 
                    value={contractor?.id} 
                  />

                  <div className={styles.formGroup}>
                    <label htmlFor="name" className={styles.controlLabel}>
                      Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        placeholder="You can leave it blank"
                      />
                      {formErrors.name && (
                        <span className={styles.errorText}>{formErrors.name}</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="document" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                      Document
                    </label>
                    <div className={styles.colSm5}>
                      <div className={styles.inputImage}>
                        <input
                          type="file"
                          id="document"
                          name="document"
                          onChange={handleFileChange}
                          className={styles.formControl}
                          accept={getDocumentAcceptValue()}
                        />
                        {formErrors.document && (
                          <span className={styles.errorText}>{formErrors.document}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.clearfix}></div>
                </fieldset>

                <div className={styles.formGroup}>
                  <div className={styles.textRight}>
                    <button
                      type="submit"
                      className={styles.btnHead}
                      disabled={submitting}
                    >
                      <i className={styles.glyphiconPlus}></i>
                      {submitting ? "Adding document..." : "Add document"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorDocumentCreate; 